import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import './SignaturePad.css';

interface SignaturePadProps {
  onSign: (signatureData: { dataUrl: string; signedBy: string }) => void;
  disabled?: boolean;
  initialName?: string;
  hideSubmitButton?: boolean;
  /** When set (typically with hideSubmitButton), keep the parent draft in sync as the pad/name change. */
  onChange?: (signatureData: { dataUrl: string; signedBy: string } | null) => void;
  /** Sample / demo portals: pad is visible but not open for a real signature. */
  demoOnly?: boolean;
  /** Diagonal hatch overlay — pad is visible but cannot be signed. */
  hatched?: boolean;
  hatchLabel?: string;
}

const SignaturePad: React.FC<SignaturePadProps> = ({
  onSign,
  disabled = false,
  initialName = '',
  hideSubmitButton = false,
  onChange,
  demoOnly = false,
  hatched = false,
  hatchLabel,
}) => {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [typedName, setTypedName] = useState(initialName);
  const [hasSignature, setHasSignature] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 150 });

  useEffect(() => {
    const updateSize = () => {
      const container = containerRef.current;
      if (container) {
        const width = container.clientWidth - 4; // -4 for border
        setCanvasSize({ width: Math.max(width, 200), height: Math.min(width * 0.5, 200) });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const emitDraft = (hasInk: boolean, name: string) => {
    if (!onChange) return;
    if (!hasInk || !name.trim() || !sigCanvas.current || sigCanvas.current.isEmpty()) {
      onChange(null);
      return;
    }
    onChange({
      dataUrl: sigCanvas.current.toDataURL('image/png'),
      signedBy: name.trim(),
    });
  };

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setHasSignature(false);
      emitDraft(false, typedName);
    }
  };

  const handleEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setHasSignature(true);
      emitDraft(true, typedName);
    }
  };

  const handleAcceptSignature = () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) return;
    if (!typedName.trim()) return;
    const dataUrl = sigCanvas.current.toDataURL('image/png');
    onSign({ dataUrl, signedBy: typedName.trim() });
  };

  const handleSubmit = () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      alert('Please provide your signature');
      return;
    }

    if (!typedName.trim()) {
      alert('Please type your full name to confirm');
      return;
    }

    const dataUrl = sigCanvas.current.toDataURL('image/png');
    onSign({
      dataUrl,
      signedBy: typedName.trim(),
    });
  };

  const isHatched = demoOnly || hatched;
  const locked = disabled || isHatched;
  const overlayLabel = hatchLabel || (demoOnly ? 'Demo only' : 'Locked');

  return (
    <div className={`signature-pad${disabled && !isHatched ? ' disabled' : ''}${isHatched ? ' signature-pad--demo' : ''}`}>
      <div className="signature-instructions">
        <p>
          {demoOnly
            ? 'This is a sample proposal — signatures are not collected on the demo.'
            : hatched
              ? 'Signing unlocks after you approve the concept model in your portal.'
              : 'Please sign below using your mouse or finger, then type your full name to confirm.'}
        </p>
      </div>

      <div className="signature-canvas-container" ref={containerRef}>
        {isHatched ? (
          <div className="signature-placeholder signature-placeholder--demo">{overlayLabel}</div>
        ) : (
          <>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{
                width: canvasSize.width,
                height: canvasSize.height,
                className: 'signature-canvas',
              }}
              onEnd={handleEnd}
            />
            {!hasSignature && (
              <div className="signature-placeholder">
                Sign here
              </div>
            )}
          </>
        )}
      </div>

      {!isHatched && (
        <>
      <div className="signature-actions">
        <button
          type="button"
          className="clear-btn"
          onClick={handleClear}
          disabled={locked}
        >
          Clear
        </button>
        {hideSubmitButton && !onChange && (
          <button
            type="button"
            className="accept-signature-btn"
            onClick={handleAcceptSignature}
            disabled={locked || !hasSignature || !typedName.trim()}
          >
            Accept Signature
          </button>
        )}
      </div>

      <div className="signature-confirmation">
        <label htmlFor="typed-name">Type your full legal name:</label>
        <input
          id="typed-name"
          type="text"
          value={typedName}
          onChange={(e) => {
            const next = e.target.value;
            setTypedName(next);
            emitDraft(hasSignature, next);
          }}
          placeholder="John Doe"
          disabled={locked}
        />
      </div>

      {!hideSubmitButton && (
        <button
          type="button"
          className="submit-signature-btn"
          onClick={handleSubmit}
          disabled={locked || !hasSignature || !typedName.trim()}
        >
          Sign & Accept Terms
        </button>
      )}

      <p className="signature-legal-text">
        By signing above, you acknowledge that this electronic signature is legally binding 
        and has the same legal effect as a handwritten signature.
      </p>
        </>
      )}
    </div>
  );
};

export default SignaturePad;
